import React from 'react'
import CrudTable from '../../crud/CrudTable'
import {
    rentService as service,
    usercompaniesService,
    billService,
} from '../../../services'
import FileDownloadIcon from '@mui/icons-material/PictureAsPdfOutlined'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material'
import { toast, ToastContainer } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { resolveBlock } from '../../common/blockAction'
import { useBlockToast } from '../../common/BlockFeedback'

const moment = require('moment')

const initFilter = {
    car: null,
    user: null,
    paymentState: null,
    from: null,
    to: null,
    rentalState: null,
    suscription: false,
}

const CANCELABLE_STATES = ['RESERVED', 'CONFIRMRESERVED', 'PENDING', 'PENDPAY']

export default function Rent() {
    const { t } = useTranslation()
    const { showBlock } = useBlockToast()
    const [refreshKey, setRefreshKey] = React.useState(0)
    const [cancelLoading, setCancelLoading] = React.useState(false)
    const [openCancelDialog, setOpenCancelDialog] = React.useState(false)
    const [selectedRent, setSelectedRent] = React.useState<any | null>(null)
    // Config de recargo de cancelación (empresa), leída del backend para no hardcodear los valores
    // del aviso. Fallback defensivo 49/24; el backend aplica el recargo real igualmente.
    const [cancelCfg, setCancelCfg] = React.useState<{
        amount: number
        hours: number
    }>({ amount: 49, hours: 24 })
    React.useEffect(() => {
        service.cancelConfig((data) => {
            if (data && data.data) {
                setCancelCfg({
                    amount:
                        data.data.cancelRCompanyTax != null
                            ? Number(data.data.cancelRCompanyTax)
                            : 49,
                    hours:
                        data.data.cancelRMaxHourCompany != null
                            ? Number(data.data.cancelRMaxHourCompany)
                            : 24,
                })
            }
        })
    }, [])

    const createEstructureFilter = [
        {
            type: 'autocomplete',
            name: 'user',
            service: usercompaniesService.combouser,
            col: 4,
        },
        {
            type: 'input',
            name: 'from',
            inputype: 'date',
            col: 4,
        },
        {
            type: 'input',
            name: 'to',
            inputype: 'to',
            col: 4,
        },
    ]

    const isFutureRent = (rent: any) => {
        if (!rent?.start) return false
        const start = new Date(rent.start)
        return !Number.isNaN(start.getTime()) && start.getTime() > Date.now()
    }

    const isCancelableRent = (rent: any) => {
        const state = rent?.rentalState || ''
        return !!rent?.id && CANCELABLE_STATES.includes(state) && isFutureRent(rent)
    }

    const openCancelModal = (rent: any) => {
        if (!isCancelableRent(rent)) return
        setSelectedRent(rent)
        setOpenCancelDialog(true)
    }

    const closeCancelModal = () => {
        if (cancelLoading) return
        setOpenCancelDialog(false)
        setSelectedRent(null)
    }

    const confirmCancelRent = () => {
        if (!selectedRent?.id) return

        setCancelLoading(true)
        service.cancel(selectedRent.id, null, (data, error) => {
            setCancelLoading(false)
            setOpenCancelDialog(false)
            setSelectedRent(null)

            // MOBD-66: el toast de éxito solo si de verdad hubo dato y ningún error.
            if (data && !error) {
                toast.success(t('rent.cancel.success'), {
                    position: toast.POSITION.TOP_CENTER,
                })
                setRefreshKey((current) => current + 1)
                return
            }

            // MOBD-66: si el backend adjunta un bloqueo de usuario, se muestra el aviso enriquecido
            // (motivo + acción) que ya trae redactado; sin bloqueo, se mantiene el toast de siempre.
            const blockInfo = resolveBlock(error)
            if (blockInfo) {
                showBlock(blockInfo)
                return
            }

            toast.error(error?.mes ? t(error.mes) : t('rent.cancel.error'), {
                position: toast.POSITION.TOP_CENTER,
            })
        })
    }

    const renderCancelSummary = () => {
        let base = t('rent.cancel.confirmGeneric')
        if (selectedRent) {
            if (selectedRent.start) {
                base = `${t('rent.cancel.confirmByDate')} ${moment(
                    new Date(selectedRent.start)
                ).format('DD/MM/YYYY HH:mm')}`
            } else if (selectedRent.user?.name) {
                base = `${t('rent.cancel.confirmByUser')} ${selectedRent.user.name}`
            } else {
                base = `${t('rent.cancel.confirmById')} #${selectedRent.id}`
            }
        }

        // Aviso de recargo solo si la cancelación es con menos del umbral de horas de antelación.
        const under =
            !!selectedRent?.start &&
            new Date(selectedRent.start).getTime() - Date.now() <
                cancelCfg.hours * 3600000

        return (
            <>
                {base}
                {under && (
                    <span
                        style={{
                            display: 'block',
                            marginTop: '12px',
                            color: '#c62828',
                            fontWeight: 'bold',
                        }}
                    >
                        {t('rent.cancel.surchargeWarning', {
                            hours: cancelCfg.hours,
                            amount: cancelCfg.amount,
                        })}
                    </span>
                )}
            </>
        )
    }
    
    const paintCell = {
        carortype: (value, object) => {
            if (object['car'] && object['car']['id']) {
                return (
                    <>
                        <span>{object['car']['brand']}</span>
                        <br />
                        <span>{object['car']['model']}</span>
                        <br />
                        <span>{object['car']['number']}</span>
                    </>
                )
            } else if (object['carTypes'] && object['carTypes']['id']) {
                return (
                    <>
                        <span style={{ color: 'orange' }}>
                            {object['carTypes']['name']}
                        </span>
                    </>
                )
            } else {
                return <></>
            }
        },
        seguro: (value, object) => {
            return value ? (
                <>
                    <img src="/ic_secbig.png" style={{ width: '30px' }} />
                    <br />
                </>
            ) : (
                <></>
            )
        },
        km_real: (value, object) => {
            return <>{value ? value.toFixed(2) : 0} Km</>
        },
        paymentState: (value, object) => {
            if (value == 'NONE') {
                return <span style={{ color: 'black' }}>No realizado</span>
            } else if (value == 'PAYMENT') {
                return <span style={{ color: 'green' }}>Pagado</span>
            } else if (value == 'PARCIAL') {
                return <span style={{ color: 'orange' }}>Pago parcial</span>
            } else if (value == 'ERROR') {
                return <span style={{ color: 'red' }}>Error en el pago</span>
            } else {
                ;<></>
            }
        },
        rentalState: (value, object) => {
            if (value == 'RESERVED') {
                return <span style={{ color: 'black' }}>Reservado</span>
            } else if (value == 'CONFIRMRESERVED') {
                return (
                    <span style={{ color: 'black' }}>Reserva confirmada</span>
                )
            } else if (value == 'ASSIGNCAR') {
                return <span style={{ color: 'black' }}>Coche asignado</span>
            } else if (value == 'START') {
                return <span style={{ color: 'green' }}>Iniciado</span>
            } else if (value == 'END') {
                return <span style={{ color: 'green' }}>Finalizado</span>
            } else if (value == 'ISSUE') {
                return <span style={{ color: 'red' }}>Con incidencia</span>
            } else if (value == 'CANCEL') {
                return <span style={{ color: 'orange' }}>Cancelado</span>
            } else if (value == 'PENDING') {
                return <span style={{ color: 'orange' }}>Pendiente</span>
            } else if (value == 'PENDPAY') {
                return (
                    <span style={{ color: 'orange' }}>Pendiente de pago</span>
                )
            } else {
                ;<></>
            }
        },
        start: (value, object) => {
            if (value) {
                return (
                    <>{moment(new Date(value)).format('DD/MM/YYYY HH:mm:ss')}</>
                )
            }
        },
        end: (value, object) => {
            if (value) {
                return (
                    <>{moment(new Date(value)).format('DD/MM/YYYY HH:mm:ss')}</>
                )
            }
        },
        start_real: (value, object) => {
            if (value) {
                return (
                    <>{moment(new Date(value)).format('DD/MM/YYYY HH:mm:ss')}</>
                )
            }
        },
        end_real: (value, object) => {
            if (value) {
                return (
                    <>{moment(new Date(value)).format('DD/MM/YYYY HH:mm:ss')}</>
                )
            }
        },
        billed: (value, object) => {
            if (object['idBill']) {
                return (
                    <Button
                        variant="contained"
                        style={{
                            backgroundColor: '#83c63b',
                            color: 'white',
                        }}
                        onClick={() => {
                            billService.print(
                                object['idBill'],
                                'Factura-Rent-' + object['id'] + '.pdf',
                                (data, error) => {}
                            )
                        }}
                    >
                        {' '}
                        <FileDownloadIcon />
                    </Button>
                )
            } else {
                return <></>
            }
        },
        actions: (value, object) => {
            if (!isCancelableRent(object)) {
                return <span style={{ color: '#9e9e9e' }}>—</span>
            }

            return (
                <Button
                    variant="contained"
                    color='secondary'
                    size="small"
                    onClick={() => openCancelModal(object)}
                    disabled={cancelLoading}
                >
                    {t('rent.cancel.action')}
                </Button>
            )
        },
    }

    const conditionDelete = (rent) => {
        var isGenerate =
            rent &&
            rent.id &&
            rent.rentalState != '' &&
            rent.rentalState == 'CANCEL'
        return isGenerate
    }
    return (
        <>
            <CrudTable
                key={refreshKey}
                service={service}
                urledit={'/rent/edit/'}
                urlnew={'/rent/new/'}
                filter={initFilter}
                hiddenButton={false}
                deleteaction={false}
                conditionDelete={conditionDelete}
                editable={false}
                notexport={true}
                paintCell={paintCell}
                rowsPerPage={30}
                i18n={'rent'}
                exportfields={[
                    'user.email',
                    'user.name',
                    'user.lastname',
                    'user.nif',
                    'car.number',
                    'start_real',
                    'end_real',
                    'total',
                ]}
                fieldstable={[
                    'user.email|user.name|user.lastname',
                    'carortype',
                    'seguro',
                    'start',
                    'end',
                    'rentalState',
                    'start_real',
                    'end_real',
                    'total',
                    'km_real',
                    'actions',
                ]}
                labels={{
                    'user.email|user.name|user.lastname': 'Conductor',
                    carortype: 'Vehículo',
                    actions: t('rent.field.actions'),
                }}
                estructureForm={createEstructureFilter}
            />

            <Dialog open={openCancelDialog} onClose={closeCancelModal}>
                <DialogTitle>{t('rent.cancel.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{renderCancelSummary()}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCancelModal}>{t('actions.cancel')}</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmCancelRent}
                        disabled={cancelLoading}
                    >
                        {t('actions.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
            <ToastContainer />
        </>
    )
}
